import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen,
  Terminal,
  Package,
  Network,
  KeyRound,
  Search,
  ChevronRight,
  ExternalLink,
  Copy,
  Check,
  MessageSquare,
  Play,
  HardDrive,
  Globe,
  Puzzle,
  RefreshCw,
  MoveRight,
} from "lucide-react";
import { PageLayout } from "../components/PageLayout";

const SECTIONS = [
  {
    id: "intro",
    icon: BookOpen,
    label: "Bienvenida",
    group: "Primeros pasos",
  },
  {
    id: "pterodactyl",
    icon: Terminal,
    label: "Panel Pterodactyl",
    group: "Game Servers",
  },
  {
    id: "consola",
    icon: Play,
    label: "Consola",
    group: "Game Servers",
  },
  {
    id: "modpacks",
    icon: Package,
    label: "Modpacks Minecraft",
    group: "Game Servers",
  },
  {
    id: "backups",
    icon: HardDrive,
    label: "Backups",
    group: "Gestión",
  },
  {
    id: "subdominios",
    icon: Globe,
    label: "Subdominios",
    group: "Gestión",
  },
  {
    id: "plugins",
    icon: Puzzle,
    label: "Instalar Plugins",
    group: "Gestión",
  },
  {
    id: "cambiar-software",
    icon: RefreshCw,
    label: "Cambiar Software",
    group: "Gestión",
  },
  {
    id: "migracion",
    icon: MoveRight,
    label: "Migración de Host",
    group: "Gestión",
  },
  {
    id: "conexion",
    icon: Network,
    label: "FTP / SFTP",
    group: "Conectividad",
  },
  {
    id: "vps-ssh",
    icon: KeyRound,
    label: "Acceso SSH a VPS",
    group: "Conectividad",
  },
];

const CodeBlock = ({ children, label = "bash" }) => {
  const [copied, setCopied] = useState(false);
  const text = typeof children === "string" ? children : "";
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to copy to clipboard:', error);
      }
    }
  };
  return (
    <div
      className="relative rounded-xl overflow-hidden my-4"
      style={{ background: "var(--osx-surface-2)", border: "1px solid var(--osx-border-strong)" }}
    >
      <div
        className="flex items-center justify-between px-3 py-1.5"
        style={{ background: "color-mix(in oklab, var(--osx-brand-600) 8%, transparent)", borderBottom: "1px solid var(--osx-border)" }}
      >
        <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--osx-text-muted)" }}>
          {label}
        </span>
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded-md"
          style={{ background: "var(--osx-surface)", color: "var(--osx-text)", border: "1px solid var(--osx-border)" }}
        >
          {copied ? <Check size={11} /> : <Copy size={11} />}
          {copied ? "copiado" : "copiar"}
        </button>
      </div>
      <pre className="p-4 text-xs sm:text-sm font-mono overflow-x-auto" style={{ color: "var(--osx-text)" }}>
        <code>{children}</code>
      </pre>
    </div>
  );
};

const SectionIntro = () => (
  <article>
    <h2 className="text-3xl font-bold tracking-tight mb-4" style={{ color: "var(--osx-text)" }}>Bienvenido a OptiShield X</h2>
    <p className="leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      En esta sección encontrarás todo lo necesario para gestionar tus servicios. Seleccioná un tema en el menú de la izquierda para comenzar.
    </p>
    <h3 className="mt-8 text-xl font-bold" style={{ color: "var(--osx-text)" }}>¿Qué necesito saber?</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Dependiendo del servicio que hayas adquirido, recibirás tus credenciales por correo electrónico. Asegurate de revisar tu bandeja de spam.
    </p>

    <div className="mt-8 grid sm:grid-cols-2 gap-4">
      {[
        { title: "Panel Pterodactyl", desc: "Gestioná tu game server con consola, archivos y backups.", target: "pterodactyl" },
        { title: "VPS · SSH", desc: "Accedé por SSH a tu VPS Linux con credenciales root.", target: "vps-ssh" },
        { title: "Modpacks Minecraft", desc: "Instalá Forge, Fabric y modpacks en 1 clic.", target: "modpacks" },
        { title: "FTP / SFTP", desc: "Subí y descargá archivos con FileZilla o WinSCP.", target: "conexion" },
      ].map((c) => (
        <div
          key={c.title}
          className="p-4 rounded-xl"
          style={{ background: "var(--osx-surface-2)", border: "1px solid var(--osx-border)" }}
        >
          <div className="text-sm font-bold" style={{ color: "var(--osx-text)" }}>{c.title}</div>
          <div className="mt-1 text-xs" style={{ color: "var(--osx-text-muted)" }}>{c.desc}</div>
        </div>
      ))}
    </div>
  </article>
);

const SectionPterodactyl = () => (
  <article>
    <h2 className="text-3xl font-bold tracking-tight mb-4" style={{ color: "var(--osx-text)" }}>Panel Pterodactyl</h2>
    <p className="leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Contenido en preparación.
    </p>
  </article>
);

const SectionConsola = () => (
  <article>
    <h2 className="text-3xl font-bold tracking-tight mb-4" style={{ color: "var(--osx-text)" }}>Uso básico del Panel Pterodactyl</h2>
    <p className="leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      El Panel Pterodactyl te permite controlar tu Game Server (encender, apagar, ver la consola) desde tu navegador web.
    </p>
    <h3 className="mt-8 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Pasos para entrar</h3>
    <ol className="mt-3 space-y-2 list-decimal list-inside" style={{ color: "var(--osx-text-muted)" }}>
      <li>Ve a la URL del panel que te enviamos al correo.</li>
      <li>Inicia sesión con tu usuario y contraseña.</li>
      <li>Haz clic en tu servidor para abrir la consola.</li>
    </ol>
    <p className="mt-4 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Para añadir comandos en la consola, simplemente escribilos <strong style={{ color: "var(--osx-text)" }}>sin la barra inicial</strong>. Por ejemplo:
    </p>
    <CodeBlock label="consola">{`op tunombre   # ✔ correcto
/op tunombre  # ✗ incorrecto`}</CodeBlock>

    <div
      className="mt-6 p-4 rounded-xl flex items-start gap-3"
      style={{ background: "color-mix(in oklab, var(--osx-brand-600) 8%, var(--osx-surface))", border: "1px solid var(--osx-border-strong)" }}
    >
      <span
        className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg text-white"
        style={{ background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))" }}
      >
        <Terminal size={14} />
      </span>
      <div>
        <div className="text-sm font-bold" style={{ color: "var(--osx-text)" }}>Tip</div>
        <div className="text-xs" style={{ color: "var(--osx-text-muted)" }}>
          Desde el panel podés programar backups automáticos y crear subusuarios con permisos limitados.
        </div>
      </div>
    </div>
  </article>
);

const SectionModpacks = () => (
  <article>
    <h2 className="text-3xl font-bold tracking-tight mb-4" style={{ color: "var(--osx-text)" }}>Cómo instalar Modpacks en Minecraft</h2>
    <p className="leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Aprendé a instalar modpacks populares como <strong style={{ color: "var(--osx-text)" }}>Forge</strong> o <strong style={{ color: "var(--osx-text)" }}>Fabric</strong> en un solo clic.
    </p>

    <div
      className="mt-6 relative aspect-video rounded-2xl overflow-hidden"
      style={{
        background: "#000",
        border: "1px solid var(--osx-border-strong)",
        boxShadow: "var(--osx-shadow-2)",
      }}
    >
      <iframe
        src="https://www.youtube.com/embed/dQw4w9WgXcQ?rel=0&modestbranding=1"
        title="Tutorial modpacks Minecraft"
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        className="absolute inset-0 w-full h-full"
        style={{ border: 0 }}
        data-testid="modpacks-video"
      />
    </div>

    <h3 className="mt-8 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Pasos rápidos</h3>
    <ol className="mt-3 space-y-2 list-decimal list-inside" style={{ color: "var(--osx-text-muted)" }}>
      <li>En tu servidor Pterodactyl, ve a la pestaña <strong style={{ color: "var(--osx-text)" }}>Startup</strong>.</li>
      <li>Seleccioná el egg <strong style={{ color: "var(--osx-text)" }}>Forge</strong> o <strong style={{ color: "var(--osx-text)" }}>Fabric</strong>.</li>
      <li>Indicá la versión del modpack y reiniciá el servidor.</li>
    </ol>
  </article>
);

const SectionConexion = () => (
  <article>
    <h2 className="text-3xl font-bold tracking-tight mb-4" style={{ color: "var(--osx-text)" }}>Conectarse por SFTP</h2>
    <p className="leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      El protocolo <strong style={{ color: "var(--osx-text)" }}>SFTP</strong> te permite subir, descargar y gestionar los archivos de tu servidor de forma segura usando un cliente como <strong style={{ color: "var(--osx-text)" }}>WinSCP</strong> o <strong style={{ color: "var(--osx-text)" }}>FileZilla</strong>. A continuación te mostramos cómo conectarte paso a paso.
    </p>

    {/* Paso 1 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 1 — Accedé a Ajustes (Settings)</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Ingresá a tu servidor en el Panel Pterodactyl. En el <strong style={{ color: "var(--osx-text)" }}>menú lateral izquierdo</strong>, hacé clic en <strong style={{ color: "var(--osx-text)" }}>Ajustes</strong> (Settings).
    </p>
    <div
      className="mt-4 rounded-xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-1)" }}
    >
      <img src="/assets/sftp-ajustes.png" alt="Opción Ajustes en el sidebar del panel" className="w-full h-auto" loading="lazy" />
    </div>

    {/* Paso 2 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 2 — Copiá los datos SFTP</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Dentro de Settings encontrarás la sección <strong style={{ color: "var(--osx-text)" }}>SFTP Details</strong> con los datos que necesitás para conectarte:
    </p>
    <div
      className="mt-4 rounded-xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-1)" }}
    >
      <img src="/assets/sftp-datos.png" alt="SFTP Details mostrando Server Address y Username" className="w-full h-auto" loading="lazy" />
    </div>
    <ul className="mt-4 space-y-3" style={{ color: "var(--osx-text-muted)" }}>
      <li className="flex items-start gap-2">
        <span className="shrink-0 mt-1 h-5 w-5 rounded-md flex items-center justify-center text-[10px] font-bold text-white" style={{ background: "var(--osx-brand-600)" }}>1</span>
        <span><strong style={{ color: "var(--osx-text)" }}>Server Address:</strong> La dirección y puerto del servidor SFTP (ej: <code className="px-1.5 py-0.5 rounded text-xs" style={{ background: "var(--osx-surface-2)", color: "var(--osx-text)" }}>sftp://mia-02.optishieldx.com:2022</code>).</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="shrink-0 mt-1 h-5 w-5 rounded-md flex items-center justify-center text-[10px] font-bold text-white" style={{ background: "var(--osx-brand-600)" }}>2</span>
        <span><strong style={{ color: "var(--osx-text)" }}>Username:</strong> Tu nombre de usuario SFTP (ej: <code className="px-1.5 py-0.5 rounded text-xs" style={{ background: "var(--osx-surface-2)", color: "var(--osx-text)" }}>angelgomez.3141b6e7</code>).</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="shrink-0 mt-1 h-5 w-5 rounded-md flex items-center justify-center text-[10px] font-bold text-white" style={{ background: "var(--osx-brand-600)" }}>3</span>
        <span><strong style={{ color: "var(--osx-text)" }}>Password:</strong> Tu contraseña es <strong style={{ color: "var(--osx-text)" }}>la misma que usás para ingresar al panel</strong>.</span>
      </li>
    </ul>
    <p className="mt-3 text-sm" style={{ color: "var(--osx-text-muted)" }}>
      También podés hacer clic en <strong style={{ color: "var(--osx-text)" }}>Launch SFTP</strong> para abrir directamente el cliente SFTP instalado en tu PC.
    </p>

    {/* Paso 3 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 3 — Conectate desde WinSCP</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Abrí <strong style={{ color: "var(--osx-text)" }}>WinSCP</strong> (o tu cliente SFTP preferido) e ingresá los datos que copiaste. Al conectar, te pedirá la contraseña de tu panel:
    </p>
    <div
      className="mt-4 rounded-xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-1)" }}
    >
      <img src="/assets/sftp-password.png" alt="WinSCP pidiendo contraseña para conectar" className="w-full h-auto" loading="lazy" />
    </div>
    <p className="mt-3 text-sm" style={{ color: "var(--osx-text-muted)" }}>
      Ingresá la contraseña y hacé clic en <strong style={{ color: "var(--osx-text)" }}>Aceptar</strong>. Si es la primera vez, WinSCP puede mostrarte una alerta de clave de host — aceptala para continuar.
    </p>

    {/* Paso 4 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 4 — Gestionar archivos</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Una vez conectado, verás el <strong style={{ color: "var(--osx-text)" }}>explorador de archivos dividido en dos paneles</strong>: a la izquierda tu PC local y a la derecha los archivos del servidor. Desde acá podés:
    </p>
    <div
      className="mt-4 rounded-xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-1)" }}
    >
      <img src="/assets/sftp-conectado.png" alt="WinSCP conectado mostrando archivos del servidor" className="w-full h-auto" loading="lazy" />
    </div>
    <ul className="mt-4 space-y-2" style={{ color: "var(--osx-text-muted)" }}>
      <li className="flex items-start gap-2">
        <span className="shrink-0 mt-0.5 text-sm">📤</span>
        <span><strong style={{ color: "var(--osx-text)" }}>Subir archivos:</strong> Arrastrá archivos desde el panel izquierdo (tu PC) al derecho (servidor).</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="shrink-0 mt-0.5 text-sm">📥</span>
        <span><strong style={{ color: "var(--osx-text)" }}>Descargar archivos:</strong> Arrastrá archivos desde el panel derecho (servidor) al izquierdo (tu PC).</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="shrink-0 mt-0.5 text-sm">✏️</span>
        <span><strong style={{ color: "var(--osx-text)" }}>Editar archivos:</strong> Hacé doble clic en cualquier archivo de texto para editarlo directamente.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="shrink-0 mt-0.5 text-sm">🗂️</span>
        <span><strong style={{ color: "var(--osx-text)" }}>Carpetas importantes:</strong> <code className="px-1 py-0.5 rounded text-xs" style={{ background: "var(--osx-surface-2)", color: "var(--osx-text)" }}>/plugins/</code> para plugins, <code className="px-1 py-0.5 rounded text-xs" style={{ background: "var(--osx-surface-2)", color: "var(--osx-text)" }}>/world/</code> para el mundo, <code className="px-1 py-0.5 rounded text-xs" style={{ background: "var(--osx-surface-2)", color: "var(--osx-text)" }}>server.properties</code> para configuración.</span>
      </li>
    </ul>

    {/* Tip */}
    <div
      className="mt-8 p-4 rounded-xl flex items-start gap-3"
      style={{ background: "color-mix(in oklab, var(--osx-brand-600) 8%, var(--osx-surface))", border: "1px solid var(--osx-border-strong)" }}
    >
      <span
        className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg text-white"
        style={{ background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))" }}
      >
        <HardDrive size={14} />
      </span>
      <div>
        <div className="text-sm font-bold" style={{ color: "var(--osx-text)" }}>Tip</div>
        <div className="text-xs" style={{ color: "var(--osx-text-muted)" }}>
          Si no tenés WinSCP instalado, podés descargarlo gratis desde <a href="https://winscp.net/eng/download.php" target="_blank" rel="noreferrer noopener" style={{ color: "var(--osx-brand-600)", textDecoration: "underline" }}>winscp.net</a>. También podés usar <strong style={{ color: "var(--osx-text)" }}>FileZilla</strong> como alternativa — los datos de conexión son los mismos.
        </div>
      </div>
    </div>
  </article>
);

const SectionVpsSsh = () => (
  <article>
    <h2 className="text-3xl font-bold tracking-tight mb-4" style={{ color: "var(--osx-text)" }}>Acceso SSH a VPS</h2>
    <p className="leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Tu VPS incluye acceso <strong style={{ color: "var(--osx-text)" }}>SSH root</strong>. Usá tu terminal preferida (macOS/Linux) o <strong style={{ color: "var(--osx-text)" }}>PuTTY</strong> en Windows.
    </p>
    <CodeBlock label="ssh">{`ssh root@TU_IP_VPS
# contraseña enviada por email al momento del despliegue`}</CodeBlock>
    <div
      className="mt-6 p-4 rounded-xl flex items-start gap-3"
      style={{ background: "color-mix(in oklab, #f59e0b 10%, var(--osx-surface))", border: "1px solid rgba(245,158,11,0.3)" }}
    >
      <span
        className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg text-white"
        style={{ background: "linear-gradient(135deg, #f59e0b, #d97706)" }}
      >
        <KeyRound size={14} />
      </span>
      <div>
        <div className="text-sm font-bold" style={{ color: "var(--osx-text)" }}>Seguridad</div>
        <div className="text-xs" style={{ color: "var(--osx-text-muted)" }}>
          Te recomendamos cambiar la contraseña por defecto y habilitar claves SSH apenas accedas por primera vez.
        </div>
      </div>
    </div>
    <p className="text-xs mt-4" style={{ color: "var(--osx-text-muted)" }}>Contenido ampliado próximamente.</p>
  </article>
);

const SectionBackups = () => (
  <article>
    <h2 className="text-3xl font-bold tracking-tight mb-4" style={{ color: "var(--osx-text)" }}>Backups</h2>
    <p className="leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      El sistema de <strong style={{ color: "var(--osx-text)" }}>backups de Pterodactyl</strong> te permite crear copias de seguridad completas de tu servidor, descargarlas, restaurarlas o bloquearlas para que no se eliminen accidentalmente.
    </p>

    {/* Paso 1 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 1 — Accedé a la sección Backups</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Ingresá a tu servidor en el Panel Pterodactyl. En el <strong style={{ color: "var(--osx-text)" }}>menú lateral izquierdo</strong>, buscá y hacé clic en la opción <strong style={{ color: "var(--osx-text)" }}>Backups</strong> (ícono de nube).
    </p>
    <div
      className="mt-4 rounded-xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-1)" }}
    >
      <img src="/assets/backups-sidebar.png" alt="Sección Backups en el sidebar" className="w-full h-auto" loading="lazy" />
    </div>

    {/* Paso 2 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 2 — Crear un nuevo backup</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Una vez en la sección de Backups, hacé clic en el botón <strong style={{ color: "var(--osx-text)" }}>Create Backup</strong> para iniciar el proceso.
    </p>
    <div
      className="mt-4 rounded-xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-1)" }}
    >
      <img src="/assets/backups-boton.png" alt="Botón Create Backup" className="w-full h-auto" loading="lazy" />
    </div>

    {/* Paso 3 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 3 — Configurar el backup</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Se abrirá el formulario <strong style={{ color: "var(--osx-text)" }}>Create server backup</strong> con las siguientes opciones:
    </p>
    <div
      className="mt-4 rounded-xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-1)" }}
    >
      <img src="/assets/backups-formulario.png" alt="Formulario de creación de backup" className="w-full h-auto" loading="lazy" />
    </div>
    <ul className="mt-4 space-y-3" style={{ color: "var(--osx-text-muted)" }}>
      <li className="flex items-start gap-2">
        <span className="shrink-0 mt-1 h-5 w-5 rounded-md flex items-center justify-center text-[10px] font-bold text-white" style={{ background: "var(--osx-brand-600)" }}>1</span>
        <span><strong style={{ color: "var(--osx-text)" }}>Backup Name:</strong> Nombre opcional para identificar el backup. Si lo dejás en blanco, se genera uno automático.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="shrink-0 mt-1 h-5 w-5 rounded-md flex items-center justify-center text-[10px] font-bold text-white" style={{ background: "var(--osx-brand-600)" }}>2</span>
        <span><strong style={{ color: "var(--osx-text)" }}>Ignored Files & Directories:</strong> Archivos o carpetas que querés excluir del backup. Dejalo vacío para respaldar todo. Soporta wildcards (ej: <code className="px-1.5 py-0.5 rounded text-xs" style={{ background: "var(--osx-surface-2)", color: "var(--osx-text)" }}>*.log</code>).</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="shrink-0 mt-1 h-5 w-5 rounded-md flex items-center justify-center text-[10px] font-bold text-white" style={{ background: "var(--osx-brand-600)" }}>3</span>
        <span><strong style={{ color: "var(--osx-text)" }}>Locked:</strong> Activá este toggle para <strong style={{ color: "var(--osx-text)" }}>bloquear el backup</strong> y evitar que sea eliminado accidentalmente. Podés desbloquearlo después.</span>
      </li>
    </ul>
    <p className="mt-3 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Cuando todo esté listo, presioná <strong style={{ color: "var(--osx-text)" }}>Start Backup</strong> y esperá a que se complete el proceso.
    </p>

    {/* Paso 4 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 4 — Opciones del backup creado</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Una vez creado el backup, aparecerá en la lista. Hacé clic en los <strong style={{ color: "var(--osx-text)" }}>tres puntos (···)</strong> para ver las opciones disponibles:
    </p>
    <div
      className="mt-4 rounded-xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-1)" }}
    >
      <img src="/assets/backups-opciones.png" alt="Opciones del backup: Download, Restore, Lock, Delete" className="w-full h-auto" loading="lazy" />
    </div>
    <ul className="mt-4 space-y-3" style={{ color: "var(--osx-text-muted)" }}>
      <li className="flex items-start gap-2">
        <span className="shrink-0 mt-0.5 text-sm">⬇️</span>
        <span><strong style={{ color: "var(--osx-text)" }}>Download:</strong> Descarga el backup como archivo comprimido a tu PC. Útil para tener una copia local fuera del servidor.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="shrink-0 mt-0.5 text-sm">🔄</span>
        <span><strong style={{ color: "var(--osx-text)" }}>Restore:</strong> Restaura el servidor al estado que tenía cuando se creó el backup. El servidor se detendrá durante el proceso.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="shrink-0 mt-0.5 text-sm">🔒</span>
        <span><strong style={{ color: "var(--osx-text)" }}>Lock:</strong> Bloquea el backup para que no pueda ser eliminado. Si ya está bloqueado, la opción cambia a <strong style={{ color: "var(--osx-text)" }}>Unlock</strong>.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="shrink-0 mt-0.5 text-sm">🗑️</span>
        <span><strong style={{ color: "var(--osx-text)" }}>Delete:</strong> Elimina el backup permanentemente. Si el backup está bloqueado, primero tenés que desbloquearlo.</span>
      </li>
    </ul>

    {/* Paso 5 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 5 — Restaurar un backup</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Al seleccionar <strong style={{ color: "var(--osx-text)" }}>Restore</strong>, aparecerá un diálogo de confirmación:
    </p>
    <div
      className="mt-4 rounded-xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-1)" }}
    >
      <img src="/assets/backups-restaurar.png" alt="Diálogo de restauración de backup" className="w-full h-auto" loading="lazy" />
    </div>
    <ul className="mt-4 space-y-3" style={{ color: "var(--osx-text-muted)" }}>
      <li className="flex items-start gap-2">
        <span className="shrink-0 mt-0.5 text-sm">⚠️</span>
        <span>El servidor se <strong style={{ color: "var(--osx-text)" }}>detendrá automáticamente</strong> durante la restauración. No podrás controlar el estado, acceder al gestor de archivos ni crear backups hasta que termine.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="shrink-0 mt-0.5 text-sm">☑️</span>
        <span><strong style={{ color: "var(--osx-text)" }}>Delete all files before restoring backup:</strong> Si activás esta opción, se borran <strong style={{ color: "var(--osx-text)" }}>todos los archivos actuales</strong> del servidor antes de restaurar. Útil para una restauración limpia. Si lo dejás desactivado, los archivos del backup se sobrescriben sobre los existentes.</span>
      </li>
    </ul>
    <p className="mt-3 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Hacé clic en <strong style={{ color: "var(--osx-text)" }}>Restore</strong> para confirmar, o en <strong style={{ color: "var(--osx-text)" }}>Cancel</strong> para volver atrás.
    </p>

    {/* Tip */}
    <div
      className="mt-8 p-4 rounded-xl flex items-start gap-3"
      style={{ background: "color-mix(in oklab, var(--osx-brand-600) 8%, var(--osx-surface))", border: "1px solid var(--osx-border-strong)" }}
    >
      <span
        className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg text-white"
        style={{ background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))" }}
      >
        <HardDrive size={14} />
      </span>
      <div>
        <div className="text-sm font-bold" style={{ color: "var(--osx-text)" }}>Tip</div>
        <div className="text-xs" style={{ color: "var(--osx-text-muted)" }}>
          Te recomendamos mantener al menos <strong style={{ color: "var(--osx-text)" }}>2 backups recientes</strong> y bloquear el más importante. Antes de instalar modpacks o hacer cambios grandes, creá siempre un backup preventivo.
        </div>
      </div>
    </div>
  </article>
);

const SectionSubdominios = () => (
  <article>
    <h2 className="text-3xl font-bold tracking-tight mb-4" style={{ color: "var(--osx-text)" }}>Crear Subdominios desde el Panel</h2>
    <p className="leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Tu panel Pterodactyl incluye un <strong style={{ color: "var(--osx-text)" }}>sistema de subdominios integrado</strong> que te permite asignar un nombre personalizado a tu servidor (ej: <code style={{ color: "var(--osx-text)" }}>miserver.optishieldx.com</code>) sin necesidad de configurar DNS manualmente.
    </p>

    {/* Paso 1 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 1 — Accedé a la sección Subdominios</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Ingresá a tu servidor en el Panel Pterodactyl. En el <strong style={{ color: "var(--osx-text)" }}>menú lateral izquierdo</strong>, buscá la opción <strong style={{ color: "var(--osx-text)" }}>Subdominios</strong> (ícono de nube). Hacé clic para abrir el gestor de subdominios.
    </p>
    <div
      className="mt-4 rounded-2xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-2)" }}
    >
      <img
        src="/assets/subdominios-sidebar.png"
        alt="Menú lateral del panel Pterodactyl mostrando la opción Subdominios"
        className="w-full h-auto"
        loading="lazy"
      />
    </div>

    {/* Paso 2 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 2 — Hacé clic en "Create subdomain"</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      En la esquina superior derecha de la página verás el botón <strong style={{ color: "var(--osx-text)" }}>Create subdomain</strong>. Hacé clic para abrir el formulario de creación.
    </p>
    <div
      className="mt-4 rounded-2xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-2)" }}
    >
      <img
        src="/assets/subdominios-crear.png"
        alt="Página de subdominios con el botón Create subdomain resaltado"
        className="w-full h-auto"
        loading="lazy"
      />
    </div>

    {/* Paso 3 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 3 — Completá el formulario</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Se abrirá un formulario con tres campos:
    </p>
    <ul className="mt-3 space-y-2 list-disc list-inside" style={{ color: "var(--osx-text-muted)" }}>
      <li><strong style={{ color: "var(--osx-text)" }}>Subdomain</strong> — Escribí el nombre que querés usar (ej: <code style={{ color: "var(--osx-text)" }}>miserver</code>).</li>
      <li><strong style={{ color: "var(--osx-text)" }}>Domain</strong> — Seleccioná el dominio disponible (ej: <code style={{ color: "var(--osx-text)" }}>optishieldx.com</code>).</li>
      <li><strong style={{ color: "var(--osx-text)" }}>Allocation</strong> — Elegí la IP y puerto asignados a tu servidor.</li>
    </ul>
    <p className="mt-2 text-sm" style={{ color: "var(--osx-text-muted)" }}>
      Cuando estés listo, presioná el botón <strong style={{ color: "var(--osx-text)" }}>Create Subdomain</strong>.
    </p>
    <div
      className="mt-4 rounded-2xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-2)" }}
    >
      <img
        src="/assets/subdominios-formulario.png"
        alt="Formulario de creación de subdominio con campos Subdomain, Domain y Allocation"
        className="w-full h-auto"
        loading="lazy"
      />
    </div>

    {/* Paso 4 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 4 — ¡Listo! Tu subdominio fue creado</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Una vez creado, verás tu subdominio en la lista con su <strong style={{ color: "var(--osx-text)" }}>nombre completo</strong> y la <strong style={{ color: "var(--osx-text)" }}>allocation</strong> asignada. Ahora podés conectarte a tu servidor usando ese subdominio en lugar de la IP.
    </p>
    <p className="mt-2 text-sm" style={{ color: "var(--osx-text-muted)" }}>
      Si necesitás eliminarlo, usá el botón <strong style={{ color: "var(--osx-text)" }}>Delete</strong> junto al subdominio.
    </p>
    <div
      className="mt-4 rounded-2xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-2)" }}
    >
      <img
        src="/assets/subdominios-resultado.png"
        alt="Subdominio creado mostrando asd.optishieldx.com con su allocation y botón Delete"
        className="w-full h-auto"
        loading="lazy"
      />
    </div>

    <div
      className="mt-8 p-4 rounded-xl flex items-start gap-3"
      style={{ background: "color-mix(in oklab, var(--osx-brand-600) 8%, var(--osx-surface))", border: "1px solid var(--osx-border-strong)" }}
    >
      <span
        className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg text-white"
        style={{ background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))" }}
      >
        <Globe size={14} />
      </span>
      <div>
        <div className="text-sm font-bold" style={{ color: "var(--osx-text)" }}>Tip</div>
        <div className="text-xs" style={{ color: "var(--osx-text-muted)" }}>
          El subdominio puede tardar unos minutos en propagarse. Si no funciona inmediatamente, esperá un momento y volvé a intentar.
        </div>
      </div>
    </div>

    <div
      className="mt-4 p-4 rounded-xl flex items-start gap-3"
      style={{ background: "color-mix(in oklab, #f59e0b 10%, var(--osx-surface))", border: "1px solid rgba(245,158,11,0.3)" }}
    >
      <span
        className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg text-white"
        style={{ background: "linear-gradient(135deg, #f59e0b, #d97706)" }}
      >
        <Globe size={14} />
      </span>
      <div>
        <div className="text-sm font-bold" style={{ color: "var(--osx-text)" }}>Importante</div>
        <div className="text-xs" style={{ color: "var(--osx-text-muted)" }}>
          Cada servidor tiene un límite de subdominios que podés crear. Si necesitás más, contactanos en <a href="https://discord.gg/ExE6pGa4df" target="_blank" rel="noreferrer noopener" style={{ color: "var(--osx-brand-600)" }}>Discord</a>.
        </div>
      </div>
    </div>
  </article>
);

const SectionPlugins = () => (
  <article>
    <h2 className="text-3xl font-bold tracking-tight mb-4" style={{ color: "var(--osx-text)" }}>Instalar Plugins desde el Panel</h2>
    <p className="leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Tu panel Pterodactyl incluye un <strong style={{ color: "var(--osx-text)" }}>instalador de plugins integrado</strong> que te permite buscar e instalar plugins directamente desde <strong style={{ color: "var(--osx-text)" }}>Modrinth</strong> sin necesidad de descargar archivos manualmente ni usar FTP.
    </p>

    {/* Paso 1 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 1 — Accedé a la sección Plugins</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Ingresá a tu servidor en el Panel Pterodactyl. En el <strong style={{ color: "var(--osx-text)" }}>menú lateral izquierdo</strong>, desplazate hacia abajo hasta encontrar la opción <strong style={{ color: "var(--osx-text)" }}>Plugins</strong> (ícono de enchufe). Hacé clic para abrir el navegador de plugins.
    </p>
    <div
      className="mt-4 rounded-2xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-2)" }}
    >
      <img
        src="/assets/plugins-sidebar.png"
        alt="Menú lateral del panel Pterodactyl mostrando la opción Plugins"
        className="w-full h-auto"
        loading="lazy"
      />
    </div>

    {/* Paso 2 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 2 — Buscá el plugin que necesitás</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Se abrirá el <strong style={{ color: "var(--osx-text)" }}>catálogo de Modrinth</strong> integrado en el panel. Desde acá podés:
    </p>
    <ul className="mt-3 space-y-2 list-disc list-inside" style={{ color: "var(--osx-text-muted)" }}>
      <li>Usar la <strong style={{ color: "var(--osx-text)" }}>barra de búsqueda</strong> para encontrar plugins por nombre (ej: "Simple Voice Chat", "LuckPerms").</li>
      <li>Filtrar por <strong style={{ color: "var(--osx-text)" }}>versión de Minecraft</strong>, tipo de loader y ordenar por relevancia.</li>
      <li>Ver la cantidad de <strong style={{ color: "var(--osx-text)" }}>descargas</strong>, likes y fecha de actualización de cada plugin.</li>
    </ul>
    <div
      className="mt-4 rounded-2xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-2)" }}
    >
      <img
        src="/assets/plugins-browser.png"
        alt="Catálogo de plugins de Modrinth integrado en el panel Pterodactyl"
        className="w-full h-auto"
        loading="lazy"
      />
    </div>

    {/* Paso 3 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 3 — Instalá el plugin</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Hacé clic en el plugin que querés instalar. Se abrirá una ventana donde podés <strong style={{ color: "var(--osx-text)" }}>seleccionar la versión</strong> del plugin. Elegí la versión compatible con tu servidor y presioná el botón <strong style={{ color: "var(--osx-text)" }}>Install Plugin</strong>.
    </p>
    <div
      className="mt-4 rounded-2xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-2)" }}
    >
      <img
        src="/assets/plugins-install.png"
        alt="Ventana de instalación de plugin mostrando selector de versión y botón Install Plugin"
        className="w-full h-auto"
        loading="lazy"
      />
    </div>

    {/* Paso 4 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 4 — Reiniciá tu servidor</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Una vez instalado, volvé a la pestaña <strong style={{ color: "var(--osx-text)" }}>Consola</strong> y reiniciá tu servidor para que el plugin se cargue correctamente. Podés verificar que se activó revisando los logs de inicio.
    </p>

    <div
      className="mt-6 p-4 rounded-xl flex items-start gap-3"
      style={{ background: "color-mix(in oklab, var(--osx-brand-600) 8%, var(--osx-surface))", border: "1px solid var(--osx-border-strong)" }}
    >
      <span
        className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg text-white"
        style={{ background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))" }}
      >
        <Puzzle size={14} />
      </span>
      <div>
        <div className="text-sm font-bold" style={{ color: "var(--osx-text)" }}>Tip</div>
        <div className="text-xs" style={{ color: "var(--osx-text-muted)" }}>
          Asegurate de que el plugin sea compatible con tu versión de Minecraft y el tipo de servidor (Spigot, Paper, etc.). Si un plugin requiere dependencias, el panel te lo indicará.
        </div>
      </div>
    </div>

    <div
      className="mt-4 p-4 rounded-xl flex items-start gap-3"
      style={{ background: "color-mix(in oklab, #f59e0b 10%, var(--osx-surface))", border: "1px solid rgba(245,158,11,0.3)" }}
    >
      <span
        className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg text-white"
        style={{ background: "linear-gradient(135deg, #f59e0b, #d97706)" }}
      >
        <Package size={14} />
      </span>
      <div>
        <div className="text-sm font-bold" style={{ color: "var(--osx-text)" }}>Método alternativo</div>
        <div className="text-xs" style={{ color: "var(--osx-text-muted)" }}>
          También podés instalar plugins manualmente: descargá el archivo <code style={{ color: "var(--osx-text)" }}>.jar</code> desde <a href="https://www.spigotmc.org/" target="_blank" rel="noreferrer noopener" style={{ color: "var(--osx-brand-600)" }}>SpigotMC</a> o <a href="https://modrinth.com/" target="_blank" rel="noreferrer noopener" style={{ color: "var(--osx-brand-600)" }}>Modrinth</a>, subilo a la carpeta <code style={{ color: "var(--osx-text)" }}>/plugins/</code> por SFTP y reiniciá el servidor.
        </div>
      </div>
    </div>
  </article>
);

const SectionCambiarSoftware = () => (
  <article>
    <h2 className="text-3xl font-bold tracking-tight mb-4" style={{ color: "var(--osx-text)" }}>Cambiar Software del Servidor</h2>
    <p className="leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Tu panel Pterodactyl incluye un <strong style={{ color: "var(--osx-text)" }}>instalador de versiones integrado</strong> que te permite cambiar entre distintos tipos de servidor (Vanilla, Paper, Spigot, Forge, Fabric y muchos más) directamente desde el navegador.
    </p>

    {/* Paso 1 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 1 — Accedé a la sección Versiones</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Ingresá a tu servidor en el Panel Pterodactyl. En el <strong style={{ color: "var(--osx-text)" }}>menú lateral izquierdo</strong>, buscá la opción <strong style={{ color: "var(--osx-text)" }}>Versiones</strong> (ícono <code style={{ color: "var(--osx-text)" }}>&lt;/&gt;</code>). Hacé clic para abrir el catálogo de software disponible.
    </p>
    <div
      className="mt-4 rounded-2xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-2)" }}
    >
      <img
        src="/assets/versiones-sidebar.png"
        alt="Menú lateral del panel Pterodactyl mostrando la opción Versiones"
        className="w-full h-auto"
        loading="lazy"
      />
    </div>

    {/* Paso 2 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 2 — Elegí el software del servidor</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Se mostrará un <strong style={{ color: "var(--osx-text)" }}>catálogo con todos los tipos de servidor disponibles</strong>. Cada uno indica la cantidad de versiones de Minecraft soportadas y el total de builds. Algunos de los más populares:
    </p>
    <ul className="mt-3 space-y-2 list-disc list-inside" style={{ color: "var(--osx-text-muted)" }}>
      <li><strong style={{ color: "var(--osx-text)" }}>Paper</strong> — El más recomendado para servidores con plugins. Optimizado y estable.</li>
      <li><strong style={{ color: "var(--osx-text)" }}>Vanilla</strong> — Minecraft sin modificaciones, ideal para una experiencia pura.</li>
      <li><strong style={{ color: "var(--osx-text)" }}>Forge / NeoForge</strong> — Para servidores con mods.</li>
      <li><strong style={{ color: "var(--osx-text)" }}>Fabric</strong> — Alternativa ligera para mods.</li>
      <li><strong style={{ color: "var(--osx-text)" }}>Spigot / Purpur</strong> — Variantes optimizadas con soporte de plugins.</li>
      <li><strong style={{ color: "var(--osx-text)" }}>Velocity / Waterfall / BungeeCord</strong> — Para proxies y redes de servidores.</li>
    </ul>
    <p className="mt-2 text-sm" style={{ color: "var(--osx-text-muted)" }}>
      Hacé clic en el software que querés usar.
    </p>
    <div
      className="mt-4 rounded-2xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-2)" }}
    >
      <img
        src="/assets/versiones-catalogo.png"
        alt="Catálogo de software de servidor disponible: Vanilla, Paper, Spigot, Forge, Fabric y más"
        className="w-full h-auto"
        loading="lazy"
      />
    </div>

    {/* Paso 3 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 3 — Seleccioná la versión de Minecraft</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Después de elegir el software, verás un <strong style={{ color: "var(--osx-text)" }}>listado con todas las versiones de Minecraft disponibles</strong> y la cantidad de builds para cada una. Seleccioná la versión que necesitás (ej: 1.21.4, 1.20.4, etc.).
    </p>
    <p className="mt-2 text-sm" style={{ color: "var(--osx-text-muted)" }}>
      También podés activar <strong style={{ color: "var(--osx-text)" }}>Show Snapshot Versions</strong> si necesitás versiones experimentales.
    </p>
    <div
      className="mt-4 rounded-2xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-2)" }}
    >
      <img
        src="/assets/versiones-lista.png"
        alt="Lista de versiones de Minecraft disponibles con cantidad de builds"
        className="w-full h-auto"
        loading="lazy"
      />
    </div>

    {/* Paso 4 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 4 — Confirmá e instalá</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Se abrirá una ventana de confirmación donde podés:
    </p>
    <ul className="mt-3 space-y-2 list-disc list-inside" style={{ color: "var(--osx-text-muted)" }}>
      <li>Seleccionar el <strong style={{ color: "var(--osx-text)" }}>build específico</strong> (ej: Build 7 [beta]).</li>
      <li>Activar o desactivar <strong style={{ color: "var(--osx-text)" }}>Wipe Server Files</strong> — esto borra todos los archivos antes de instalar. <strong style={{ color: "#f59e0b" }}>¡Esta acción no se puede deshacer!</strong></li>
    </ul>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Cuando estés listo, presioná el botón <strong style={{ color: "var(--osx-text)" }}>Install</strong>.
    </p>
    <div
      className="mt-4 rounded-2xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-2)" }}
    >
      <img
        src="/assets/versiones-install.png"
        alt="Ventana de instalación de Paper con selector de build y opción Wipe Server Files"
        className="w-full h-auto"
        loading="lazy"
      />
    </div>

    <div
      className="mt-8 p-4 rounded-xl flex items-start gap-3"
      style={{ background: "color-mix(in oklab, #f59e0b 10%, var(--osx-surface))", border: "1px solid rgba(245,158,11,0.3)" }}
    >
      <span
        className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg text-white"
        style={{ background: "linear-gradient(135deg, #f59e0b, #d97706)" }}
      >
        <RefreshCw size={14} />
      </span>
      <div>
        <div className="text-sm font-bold" style={{ color: "var(--osx-text)" }}>Advertencia</div>
        <div className="text-xs" style={{ color: "var(--osx-text-muted)" }}>
          Cambiar de software puede causar incompatibilidad con tus plugins o mods actuales. <strong style={{ color: "var(--osx-text)" }}>Hacé un backup antes de realizar este cambio.</strong> Si activás "Wipe Server Files", perderás todos los archivos del servidor.
        </div>
      </div>
    </div>

    <div
      className="mt-4 p-4 rounded-xl flex items-start gap-3"
      style={{ background: "color-mix(in oklab, var(--osx-brand-600) 8%, var(--osx-surface))", border: "1px solid var(--osx-border-strong)" }}
    >
      <span
        className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg text-white"
        style={{ background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))" }}
      >
        <RefreshCw size={14} />
      </span>
      <div>
        <div className="text-sm font-bold" style={{ color: "var(--osx-text)" }}>Tip</div>
        <div className="text-xs" style={{ color: "var(--osx-text-muted)" }}>
          Si solo querés actualizar la versión de Minecraft (ej: de 1.20.4 a 1.21.4) sin cambiar de software, seguí el mismo proceso seleccionando el mismo tipo de servidor pero con la nueva versión.
        </div>
      </div>
    </div>
  </article>
);

const SectionMigracion = () => (
  <article>
    <h2 className="text-3xl font-bold tracking-tight mb-4" style={{ color: "var(--osx-text)" }}>Migración de Host</h2>
    <p className="leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Tu nuevo panel incluye un <strong style={{ color: "var(--osx-text)" }}>sistema de migración integrado</strong> que te permite importar todos los archivos de tu servidor anterior directamente por SFTP, sin necesidad de descargar y volver a subir nada manualmente.
    </p>

    {/* Paso 1 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 1 — Obtené los datos SFTP del panel anterior</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Ingresá a tu <strong style={{ color: "var(--osx-text)" }}>panel anterior</strong> (el del host desde donde querés migrar). En el menú lateral, buscá la opción <strong style={{ color: "var(--osx-text)" }}>Settings</strong>.
    </p>
    <div
      className="mt-4 rounded-2xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-2)" }}
    >
      <img
        src="/assets/migracion-settings.png"
        alt="Menú lateral del panel anterior mostrando la opción Settings resaltada"
        className="w-full h-auto"
        loading="lazy"
      />
    </div>

    <p className="mt-4 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Dentro de Settings encontrarás la sección <strong style={{ color: "var(--osx-text)" }}>SFTP Details</strong> con los datos que necesitás:
    </p>
    <ul className="mt-3 space-y-2 list-disc list-inside" style={{ color: "var(--osx-text-muted)" }}>
      <li><strong style={{ color: "var(--osx-text)" }}>Server Address</strong> — La dirección del servidor SFTP (ej: <code style={{ color: "var(--osx-text)" }}>sftp://sftp.tuhost.com:2026</code>).</li>
      <li><strong style={{ color: "var(--osx-text)" }}>Username</strong> — Tu nombre de usuario SFTP.</li>
      <li><strong style={{ color: "var(--osx-text)" }}>Password</strong> — La misma contraseña que usás para acceder al panel.</li>
    </ul>
    <p className="mt-2 text-sm" style={{ color: "var(--osx-text-muted)" }}>
      Copiá estos datos, los necesitarás en el siguiente paso.
    </p>
    <div
      className="mt-4 rounded-2xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-2)" }}
    >
      <img
        src="/assets/migracion-sftp-datos.png"
        alt="Sección SFTP Details mostrando Server Address y Username"
        className="w-full h-auto"
        loading="lazy"
      />
    </div>

    {/* Paso 2 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 2 — Accedé a Migración en el nuevo panel</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Ahora ingresá a tu <strong style={{ color: "var(--osx-text)" }}>nuevo servidor en OptiShield X</strong>. En el menú lateral, buscá la opción <strong style={{ color: "var(--osx-text)" }}>Migración</strong> (ícono de nube con flecha). Hacé clic para abrir el formulario de conexión.
    </p>
    <div
      className="mt-4 rounded-2xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-2)" }}
    >
      <img
        src="/assets/migracion-sidebar.png"
        alt="Menú lateral del nuevo panel mostrando la opción Migración"
        className="w-full h-auto"
        loading="lazy"
      />
    </div>

    {/* Paso 3 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 3 — Completá los datos de conexión</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Se mostrará un formulario donde debés ingresar los datos SFTP que copiaste del panel anterior:
    </p>
    <ul className="mt-3 space-y-2 list-disc list-inside" style={{ color: "var(--osx-text-muted)" }}>
      <li><strong style={{ color: "var(--osx-text)" }}>Host</strong> — La dirección del servidor sin el prefijo <code style={{ color: "var(--osx-text)" }}>sftp://</code> (ej: <code style={{ color: "var(--osx-text)" }}>sftp.tuhost.com</code>).</li>
      <li><strong style={{ color: "var(--osx-text)" }}>Port</strong> — El puerto SFTP (ej: <code style={{ color: "var(--osx-text)" }}>2026</code>).</li>
      <li><strong style={{ color: "var(--osx-text)" }}>Username</strong> — El usuario que copiaste.</li>
      <li><strong style={{ color: "var(--osx-text)" }}>Password</strong> — La contraseña del panel anterior.</li>
      <li><strong style={{ color: "var(--osx-text)" }}>Source Path</strong> — La ruta de origen de los archivos (dejá <code style={{ color: "var(--osx-text)" }}>/</code> para importar todo).</li>
      <li><strong style={{ color: "var(--osx-text)" }}>Destination Path</strong> — La ruta de destino en el nuevo servidor (dejá <code style={{ color: "var(--osx-text)" }}>/</code>).</li>
    </ul>
    <p className="mt-2 text-sm" style={{ color: "var(--osx-text-muted)" }}>
      Asegurate de que el protocolo esté en <strong style={{ color: "var(--osx-text)" }}>SFTP</strong> y presioná el botón <strong style={{ color: "var(--osx-text)" }}>Connect</strong>.
    </p>
    <div
      className="mt-4 rounded-2xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-2)" }}
    >
      <img
        src="/assets/migracion-formulario.png"
        alt="Formulario de migración con campos Host, Port, Username, Password, Source Path y Destination Path"
        className="w-full h-auto"
        loading="lazy"
      />
    </div>

    {/* Paso 4 */}
    <h3 className="mt-10 text-xl font-bold" style={{ color: "var(--osx-text)" }}>Paso 4 — Seleccioná los archivos e importá</h3>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Una vez conectado, verás un listado con todos los archivos y carpetas del servidor anterior. Desde acá podés:
    </p>
    <ul className="mt-3 space-y-2 list-disc list-inside" style={{ color: "var(--osx-text-muted)" }}>
      <li>Usar <strong style={{ color: "var(--osx-text)" }}>Select All</strong> para seleccionar todo de una vez.</li>
      <li>Elegir <strong style={{ color: "var(--osx-text)" }}>manualmente</strong> solo las carpetas/archivos que necesitás (ej: mundo, plugins, configs).</li>
      <li>Activar <strong style={{ color: "var(--osx-text)" }}>Delete Existing Files</strong> si querés borrar todo lo que hay en el nuevo servidor antes de importar. <strong style={{ color: "#f59e0b" }}>¡Esta acción no se puede deshacer!</strong></li>
    </ul>
    <p className="mt-2 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
      Cuando estés listo, presioná <strong style={{ color: "var(--osx-text)" }}>Start Import</strong> y esperá a que termine la transferencia.
    </p>
    <div
      className="mt-4 rounded-2xl overflow-hidden"
      style={{ border: "1px solid var(--osx-border-strong)", boxShadow: "var(--osx-shadow-2)" }}
    >
      <img
        src="/assets/migracion-importar.png"
        alt="Ventana Select Files to Import con listado de archivos, opción Select All y Delete Existing Files"
        className="w-full h-auto"
        loading="lazy"
      />
    </div>

    <div
      className="mt-8 p-4 rounded-xl flex items-start gap-3"
      style={{ background: "color-mix(in oklab, var(--osx-brand-600) 8%, var(--osx-surface))", border: "1px solid var(--osx-border-strong)" }}
    >
      <span
        className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg text-white"
        style={{ background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))" }}
      >
        <MoveRight size={14} />
      </span>
      <div>
        <div className="text-sm font-bold" style={{ color: "var(--osx-text)" }}>Tip</div>
        <div className="text-xs" style={{ color: "var(--osx-text-muted)" }}>
          Podés guardar los datos de conexión en <strong style={{ color: "var(--osx-text)" }}>Saved Profiles</strong> para no tener que ingresarlos de nuevo si necesitás hacer otra migración.
        </div>
      </div>
    </div>

    <div
      className="mt-4 p-4 rounded-xl flex items-start gap-3"
      style={{ background: "color-mix(in oklab, #f59e0b 10%, var(--osx-surface))", border: "1px solid rgba(245,158,11,0.3)" }}
    >
      <span
        className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg text-white"
        style={{ background: "linear-gradient(135deg, #f59e0b, #d97706)" }}
      >
        <MoveRight size={14} />
      </span>
      <div>
        <div className="text-sm font-bold" style={{ color: "var(--osx-text)" }}>Asistencia</div>
        <div className="text-xs" style={{ color: "var(--osx-text-muted)" }}>
          Si tenés problemas conectándote, verificá que los datos SFTP sean correctos y que el host anterior permita conexiones externas. También podés contactarnos en <a href="https://discord.gg/ExE6pGa4df" target="_blank" rel="noreferrer noopener" style={{ color: "var(--osx-brand-600)" }}>Discord</a> para asistencia.
        </div>
      </div>
    </div>
  </article>
);

const CONTENT_MAP = {
  intro: <SectionIntro />,
  pterodactyl: <SectionPterodactyl />,
  consola: <SectionConsola />,
  modpacks: <SectionModpacks />,
  backups: <SectionBackups />,
  subdominios: <SectionSubdominios />,
  plugins: <SectionPlugins />,
  "cambiar-software": <SectionCambiarSoftware />,
  migracion: <SectionMigracion />,
  conexion: <SectionConexion />,
  "vps-ssh": <SectionVpsSsh />,
};

export const DocumentacionPage = () => {
  const [active, setActive] = useState("intro");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query.trim()) return SECTIONS;
    const q = query.toLowerCase();
    return SECTIONS.filter((s) =>
      s.label.toLowerCase().includes(q) || s.group.toLowerCase().includes(q)
    );
  }, [query]); // SECTIONS is a module constant, doesn't need to be in deps

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((s) => {
      if (!g[s.group]) g[s.group] = [];
      g[s.group].push(s);
    });
    return g;
  }, [filtered]);

  return (
    <PageLayout title="Documentación | OptiShield X" showBackgroundOrbs>
      <section className="relative osx-noise" data-testid="docs-hero">
        <div className="absolute inset-0 osx-grid-bg opacity-50" aria-hidden />
        <div className="osx-container relative pt-14 sm:pt-20 pb-10">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-5"
            style={{ background: "var(--osx-surface-2)", border: "1px solid var(--osx-border-strong)", color: "var(--osx-brand-600)" }}
          >
            <BookOpen size={13} /> Documentación oficial
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight"
            style={{ color: "var(--osx-text)" }}
            data-testid="docs-headline"
          >
            Centro de <span className="osx-gradient-text">Ayuda y Manuales</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="mt-4 max-w-2xl text-base sm:text-lg"
            style={{ color: "var(--osx-text-muted)" }}
          >
            Aprendé a configurar y exprimir al máximo tus servicios.
          </motion.p>
        </div>
      </section>

      <section className="relative pb-20">
        <div className="osx-container grid lg:grid-cols-12 gap-8 items-start">
          {/* Sidebar */}
          <aside className="lg:col-span-4 xl:col-span-3 lg:sticky lg:top-24">
            <div
              className="rounded-2xl p-4"
              style={{ background: "var(--osx-surface)", border: "1px solid var(--osx-border)", boxShadow: "var(--osx-shadow-1)" }}
            >
              <div
                className="flex items-center gap-2 px-3 py-2 rounded-xl mb-3"
                style={{ background: "var(--osx-surface-2)", border: "1px solid var(--osx-border)" }}
              >
                <Search size={14} style={{ color: "var(--osx-text-muted)" }} />
                <input
                  type="text"
                  placeholder="Buscar…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="docs-search-input"
                  className="flex-1 bg-transparent outline-none text-sm"
                  style={{ color: "var(--osx-text)" }}
                />
              </div>

              {Object.entries(grouped).map(([group, items]) => (
                <div key={group} className="mb-4 last:mb-0">
                  <div className="px-2 mb-1 text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: "var(--osx-text-muted)" }}>
                    {group}
                  </div>
                  <ul className="space-y-0.5">
                    {items.map((s) => {
                      const isActive = active === s.id;
                      return (
                        <li key={s.id}>
                          <button
                            type="button"
                            onClick={() => setActive(s.id)}
                            data-testid={`docs-nav-${s.id}`}
                            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold text-left"
                            style={{
                              background: isActive
                                ? "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))"
                                : "transparent",
                              color: isActive ? "#ffffff" : "var(--osx-text-muted)",
                              boxShadow: isActive ? "0 6px 18px rgba(2,132,199,0.25)" : "none",
                              transition: "background-color 0.2s ease, color 0.2s ease",
                            }}
                          >
                            <s.icon size={14} strokeWidth={2.2} />
                            <span className="flex-1">{s.label}</span>
                            <ChevronRight size={12} className={isActive ? "opacity-100" : "opacity-40"} />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}

              <div
                className="mt-4 p-3 rounded-xl text-xs"
                style={{ background: "var(--osx-surface-2)", border: "1px solid var(--osx-border)", color: "var(--osx-text-muted)" }}
              >
                ¿Necesitás ayuda?{" "}
                <a
                  href="https://discord.gg/ExE6pGa4df"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 font-semibold"
                  style={{ color: "var(--osx-brand-600)" }}
                  data-testid="docs-discord-help"
                >
                  <MessageSquare size={11} /> Preguntar en Discord <ExternalLink size={10} />
                </a>
              </div>
            </div>
          </aside>

          {/* Content */}
          <main className="lg:col-span-8 xl:col-span-9">
            <div
              className="rounded-2xl p-6 sm:p-10"
              style={{ background: "var(--osx-surface)", border: "1px solid var(--osx-border)", boxShadow: "var(--osx-shadow-1)" }}
              data-testid="docs-content"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.3 }}
                >
                  {CONTENT_MAP[active]}
                </motion.div>
              </AnimatePresence>
            </div>
          </main>
        </div>
      </section>
    </PageLayout>
  );
};
